/* I dati della scheda «Statistiche» delle Campagne, pronti da disegnare. Modulo PURO: niente
   React, niente axios, niente grafici — la risposta di GET /campagne/statistiche entra, ne
   escono etichette, percentuali, segmenti e colori, e si provano a tavolino.

   I colori sono SOLO variabili del tema (theme-tokens.css): niente viola, in nessuna
   sfumatura. TIM e NON TIM usano i colori che hanno in tutta l'applicazione
   (--color-tim / --color-non-tim), cosi' la stessa cosa ha lo stesso colore dappertutto. */
import { etichettaMotivo } from './campagneData';

const fmt = (n) => (n ?? 0).toLocaleString('it-IT');

/** "34%" da un rapporto fra 0 e 1; "—" se non c'e' niente su cui calcolarlo. */
export function fmtResa(resa, indagate) {
  if (indagate === 0 || resa === null || resa === undefined || Number.isNaN(Number(resa))) return '—';
  const p = Number(resa) * 100;
  if (p > 0 && p < 0.1) return '<0,1%';        // "0%" su una riga che c'e' direbbe il falso
  return `${p.toLocaleString('it-IT', { maximumFractionDigits: p > 0 && p < 10 ? 1 : 0 })}%`;
}

/** La quota di `parte` su `tutto`, fra 0 e 1 (0 se `tutto` e' zero). */
export const quota = (parte, tutto) => (tutto ? (parte || 0) / tutto : 0);

/** "12%" di `parte` su `tutto`; stringa vuota se `tutto` e' zero. */
export function percento(parte, tutto) {
  if (!tutto) return '';
  return fmtResa(quota(parte, tutto), tutto);
}

/* ── Cosa e' nato in archivio, secondo il 456 ── */
const SEG_ARCHIVIO = [
  { key: 'non_tim', label: 'NON TIM', colore: 'var(--color-non-tim)',
    spiega: 'Il 456 dice che la linea non è TIM: sono i clienti da contattare.' },
  { key: 'tim', label: 'TIM', colore: 'var(--color-tim)',
    spiega: 'Il 456 dice che la linea è già TIM.' },
  { key: 'da_verificare', label: 'In attesa del 456', colore: 'var(--faint)',
    spiega: 'Il record è nato, ma il 456 non l’ha ancora verificato.' },
  { key: 'altro', label: 'Altro esito', colore: 'var(--muted)',
    spiega: 'Il 456 ha risposto con un esito diverso (errore, servizio non disponibile…).' },
];

/** I segmenti del 456, nell'ordine fisso NON TIM · TIM · in attesa · altro. "Altro" compare
    solo se c'e': quasi sempre e' zero, e un quarto colore vuoto sarebbe solo rumore. */
export function segmentiArchivio(archivio) {
  const a = archivio || {};
  const nati = a.nati || 0;
  return SEG_ARCHIVIO
    .filter((s) => s.key !== 'altro' || (a.altro || 0) > 0)
    .map((s) => ({ ...s, valore: a[s.key] || 0, quota: quota(a[s.key], nati) }));
}

/** Fra i numeri che il 456 ha GIA' verificato (TIM o NON TIM), la quota di NON TIM. */
export function quotaNonTim(archivio) {
  const a = archivio || {};
  const verificati = (a.tim || 0) + (a.non_tim || 0);
  return verificati ? (a.non_tim || 0) / verificati : null;
}

/* ── Dove finiscono le righe ── */

// Le etichette BREVI degli esiti, per l'asse del grafico: quelle lunghe di etichettaMotivo
// finiscono nel tooltip, dove c'e' posto.
const BREVE = {
  risolta: 'Risolte',
  da_indagare: 'Da indagare',
  in_corso: 'In corso',
  scartata: 'Scartate (senza motivo)',
  non_risolta: 'Non risolte (senza motivo)',
  cellulare: 'Cellulari',
  persona_fisica: 'Persone fisiche',
  azienda_in_archivio: 'Azienda già in archivio',
  numero_in_archivio: 'Numero già in archivio',
  gia_lavorata: 'Già lavorate prima',
  nessun_sito: 'Senza sito',
  nessun_numero: 'Nessun numero trovato',
  nessun_match: 'Le cifre non combaciano',
  candidati_ambigui: 'Più numeri possibili',
  azienda_non_trovata: 'Non trovata su Maps',
  azienda_diversa: 'Un’altra azienda su Maps',
  identita_non_verificabile: 'Identità non verificabile',
  archiviazione_fallita: 'Scrittura in archivio fallita',
  piva_non_confermata: 'P.IVA non trovata sul sito',
  piva_diversa: 'Un’altra P.IVA sul sito',
  numeri_ambigui: 'Più fissi, da scegliere',
  solo_cellulari: 'Solo cellulari',
};

const SPIEGA_STATO_ESITO = {
  risolta: 'Numero ricostruito: è nato il record in archivio.',
  da_indagare: 'In attesa: l’indagine non è ancora passata.',
  in_corso: 'Il motore la sta guardando adesso.',
  scartata: 'Scartata, senza un motivo scritto.',
  non_risolta: 'Indagata ma non risolta, senza un motivo scritto.',
};

// A quale gruppo (stato) appartiene ogni motivo: e' il colore della barra e la voce della
// legenda. Gli scarti sono decisi all'ingresso o alla nascita del record; il resto e' un
// esito dell'indagine.
const MOTIVI_SCARTO = ['cellulare', 'persona_fisica', 'azienda_in_archivio',
  'numero_in_archivio', 'gia_lavorata'];

export const GRUPPI_ESITO = [
  { key: 'risolta', label: 'Risolte', colore: 'var(--ok)' },
  { key: 'non_risolta', label: 'Non risolte', colore: 'var(--warn)' },
  { key: 'scartata', label: 'Scartate', colore: 'var(--muted)' },
  { key: 'in_corso', label: 'In corso', colore: 'var(--info)' },
  { key: 'da_indagare', label: 'Da indagare', colore: 'var(--faint)' },
];

/** Il gruppo (uno stato) di una chiave di `esiti`, che puo' essere un motivo o uno stato. */
export function gruppoEsito(chiave) {
  if (GRUPPI_ESITO.some((g) => g.key === chiave)) return chiave;
  return MOTIVI_SCARTO.includes(chiave) ? 'scartata' : 'non_risolta';
}

/** Gli esiti pronti per il grafico: raggruppati per stato (nell'ordine di GRUPPI_ESITO) e,
    dentro il gruppo, dal piu' numeroso. Gli zeri non ci sono (il backend li toglie gia'). */
export function righeEsiti(esiti, totaleRighe) {
  const ordine = GRUPPI_ESITO.map((g) => g.key);
  return (esiti || [])
    .filter((e) => e && e.chiave && e.quante > 0)
    .map((e) => {
      const gruppo = gruppoEsito(e.chiave);
      const g = GRUPPI_ESITO.find((x) => x.key === gruppo);
      return {
        chiave: e.chiave,
        breve: BREVE[e.chiave] || etichettaMotivo(e.chiave),
        lungo: SPIEGA_STATO_ESITO[e.chiave] || etichettaMotivo(e.chiave),
        gruppo, gruppoLabel: g.label, colore: g.colore,
        quante: e.quante, quota: quota(e.quante, totaleRighe),
      };
    })
    .sort((a, b) => (ordine.indexOf(a.gruppo) - ordine.indexOf(b.gruppo)) || (b.quante - a.quante));
}

/* ── Persone fisiche ── */

/** Quante persone fisiche su tutte le campagne (la somma dell'elenco `campagne`, che e'
    sempre completo) e quante in ogni lista, dalla piu' recente. */
export function personeFisiche(stat) {
  const campagne = (stat && stat.campagne) || [];
  return {
    tutte: campagne.reduce((n, c) => n + (c.persone_fisiche || 0), 0),
    righeTutte: campagne.reduce((n, c) => n + (c.righe || 0), 0),
    perCampagna: campagne.map((c) => ({
      id: c.id, nome: c.nome, quante: c.persone_fisiche || 0, righe: c.righe || 0,
      quota: quota(c.persone_fisiche, c.righe),
    })),
  };
}

/* ── Regioni e province ── */

const PICCOLE = new Set(['e', 'di', 'del', 'della', 'dell', 'd']);

/** "FRIULI VENEZIA GIULIA" -> "Friuli Venezia Giulia", "MONZA E BRIANZA" -> "Monza e Brianza",
    "VALLE D'AOSTA" -> "Valle d'Aosta". "NON ASSEGNATA" si dice per esteso. */
export function nomeLuogo(nome) {
  const s = String(nome || '').trim();
  if (!s || s === 'NON ASSEGNATA') return 'Non assegnata';
  return s.toLowerCase().split(/(\s+|-|')/).map((p, i) => {
    if (!p || /^(\s+|-|')$/.test(p)) return p;
    if (i > 0 && PICCOLE.has(p)) return p;
    return p.charAt(0).toUpperCase() + p.slice(1);
  }).join('');
}

/* ── Identita' delle risolte ── */
const SEG_IDENTITA = [
  { key: 'nome', label: 'Per nome', colore: 'var(--ok)',
    spiega: 'Il nome sulla scheda di Maps combacia con la ragione sociale: la prova piena.' },
  { key: 'comune', label: 'Solo per comune', colore: 'var(--warn)',
    spiega: 'Il nome non si è potuto leggere: l’azienda è stata riconosciuta dal comune. È la prova più debole.' },
  { key: 'nessuna', label: 'Non verificata', colore: 'var(--err)',
    spiega: 'Né nome né comune: il numero è entrato in archivio per decisione di una persona.' },
];

/** I segmenti dell'identita' fra le risolte. "Non verificata" compare solo se c'e': una
    risolta senza identita' nasce solo da una promozione a mano. */
export function segmentiIdentita(identita) {
  const i = identita || {};
  const tot = (i.nome || 0) + (i.comune || 0) + (i.nessuna || 0);
  return SEG_IDENTITA
    .filter((s) => s.key !== 'nessuna' || (i.nessuna || 0) > 0)
    .map((s) => ({ ...s, valore: i[s.key] || 0, quota: quota(i[s.key], tot) }));
}

/** I raggi degli angoli di ogni segmento di una barra composta orizzontale: arrotondato
    solo alle estremita' della parte PIENA (i segmenti a zero non contano). */
export function raggiSegmenti(segmenti, r = 4) {
  const pieni = segmenti.map((s, i) => (s.valore > 0 ? i : -1)).filter((i) => i >= 0);
  const primo = pieni[0];
  const ultimo = pieni[pieni.length - 1];
  return segmenti.map((s, i) => [
    i === primo ? r : 0, i === ultimo ? r : 0, i === ultimo ? r : 0, i === primo ? r : 0,
  ]);
}

export { fmt };
